<template>
  <EditionModal :is-displayed="!!problemId" :query-param="ishikawaModalQueryParam" class="ishikawa">
    <FiltersZone ref="filtersZone">
      <h2 class="basic-sub-title">
        {{ $t('message.ISHI_BRANCHLESS_CAUSES') }}
      </h2>
      <div v-if="branchlessCauses.length === 0" class="field-hint">
        {{ $t('message.ISHI_NO_BRANCHLESS_CAUSES') }}
      </div>
      <div v-if="loadingDelayed">
        <IshikawaCause
          v-for="cause in branchlessCauses"
          :key="`cause${cause.id}-${causeKeyModifier}`"
          :problem-id="problemId"
          :cause="cause"
          :container="$el"
          @drag-started="unassignedCauseDragging"
          @showActionsPerCauseModal="$emit('showActionsPerCauseModal', $event)"
        />
      </div>
    </FiltersZone>
    <div v-if="!!problemId" class="ishikawa__wrapper">
      <div class="ishikawa__content">
        <div class="ishikawa__hint">
          <div class="v-centerer">
            <Icon>{{ infosMedium }}</Icon>
            <!-- eslint-disable-next-line vue/no-v-html -->
            <div v-html="$t('message.ISHI_HINT')" />
          </div>
          <div class="v-centerer mt10em">
            <div>
              {{ $t('help.W5_CAUSE_SELECT_HINT') }}
            </div>
            <button
              v-if="sheetSelection"
              class="generic-border generic-border--text-btn generic-border--gray-border ishikawa__toggle-sheet"
              @click="toggleSheetSelection(true)"
            >
              <div>{{ $t('message.CANCEL') }}</div>
            </button>
            <button
              v-else
              class="generic-border generic-border--text-btn generic-border--green-border ishikawa__toggle-sheet"
              @click="toggleSheetSelection(true)"
            >
              <div>{{ $t('message.DISPLAY_IN_SHEET_SHORT') }}</div>
            </button>
          </div>
        </div>

        <!-- Fishbone diagram -->
        <div v-if="loadingDelayed" ref="content" class="ishikawa__content__wrapper">
          <canvas ref="canvas" />
          <div ref="fishBody" class="fish-body">
            <div class="top-branches">
              <IshikawaBranch
                v-for="branch in topBranches"
                :ref="`branch${branch.id}`"
                :key="branch.id"
                :problem-id="problemId"
                :branch="branch"
                :container="$el"
                :height="branchHeight"
                :sheet-selection="sheetSelection"
                @margin-set="causeSizeUpdate"
                @size-update="causeSizeUpdate"
                @showActionsPerCauseModal="$emit('showActionsPerCauseModal', $event)"
              />
            </div>
            <div class="bottom-branches">
              <IshikawaBranch
                v-for="branch in bottomBranches"
                :ref="`branch${branch.id}`"
                :key="branch.id"
                :problem-id="problemId"
                :branch="branch"
                :container="$el"
                :height="branchHeight"
                :sheet-selection="sheetSelection"
                is-bottom
                @margin-set="causeSizeUpdate"
                @size-update="causeSizeUpdate"
                @showActionsPerCauseModal="$emit('showActionsPerCauseModal', $event)"
              />
            </div>
          </div>
          <div class="fish-head" :style="fishHeadStyle">
            <EllipsisDiv :text="decodeHTML(stateProblem ? stateProblem.lWhat : '')" />
          </div>
        </div>
      </div>
      <div v-show="sheetSelection" class="sheet-selection-bg">
        <h3>
          {{ $t("message.SELECT_ROOT_CAUSES") }}
        </h3>
      </div>
    </div>

    <div ref="deleteZoneWrapper" class="ishikawa__delete-zone v-centerer">
      <div
        ref="deleteZone"
        class="generic-border generic-border--text-btn generic-border--clickable generic-border--red-dashed-border"
      >
        <Icon>{{ deleteMedium }}</Icon>
        <span>{{ $t("message.DELETE") }}</span>
      </div>
    </div>
  </EditionModal>
</template>

<script>
/**
 * Ishikawa diagram edition modal.
 * Summary of the current lifecycle:
 * MOUNT    IshikawaCause
 *              - calculateMargin
 *              - drawLinks
 * MOUNT    IshikawaBranch
 * UPDATE   IshikawaCause
 *              - calculateMargin
 *              - drawLinks
 * MOUNT    IshikawaEditionModal
 *              - drawCanvas
 *
 */

import toastr from 'toastr';
import $ from 'jquery';
import { cloneDeep, isEqual } from 'lodash';
// Vuex
import { DELETE_CAUSE } from '@/store/modules/problem/action-types';
// Components
import EditionModal from '@/components/edition-modals/edition-modal.vue';
import EllipsisDiv from '@/components/ellipsize.vue';
import FiltersZone from '@/components/filters-zone.vue';
import Icon from '@/components/icon.vue';
import IshikawaBranch from '@/components/ishikawa-branch.vue';
import IshikawaCause from '@/components/ishikawa-cause.vue';
// Utils
import mixinSocketHandler from '@/mixins/socket-handler.vue';
import SocketMessages from '@/utils/classes/SocketMessages';
import { fishHeadMargin, branchAngleTan, branchHeadHeight } from '@/utils/ishikawa-utils';
import {
  calert,
  decodeHTML,
  detectEscapeKey,
  undetectEscapeKey,
} from '@/utils/main';
import { ishikawaModalQueryParam } from '@/utils/navigation-constants';
import { deleteMedium, infosMedium } from '@/assets/icons';
import { SColors } from '@/utils/constants';
import IAHelp from '@/utils/classes/IAHelp';
import IAHelpStep from '@/utils/classes/IAHelpStep';


function ishikawaInitialData() {
  return {
    // Margin between top/bottom sections and the center
    sectionMargin: 2,
    // Height of the biggest branch
    branchHeight: 0,
    causeKeyModifier: 1,
    // y position of the Ishikawa core in pixels
    yCoreLine: 0,
    coreWidth: 0,
    sheetSelection: false,
    // Load the content with a delay to prevent lagging on button click
    loadingDelayed: false,
  };
}

export default {

  name: 'IshikawaEditionModal',

  components: {
    EditionModal,
    EllipsisDiv,
    FiltersZone,
    Icon,
    IshikawaBranch,
    IshikawaCause,
  },

  mixins: [mixinSocketHandler],

  data() {
    return {
      deleteMedium,
      infosMedium,
      ishikawaModalQueryParam,
      ...ishikawaInitialData(),
      // Properties used by other components. See src/views/readme.md
      pageBackButtonAction: () => {
        this.$router.back();
      },
    };
  },

  computed: {
    /** Number: ID of the problem */
    problemId() {
      return this.$route.query[ishikawaModalQueryParam] === undefined
        ? null
        : Number(this.$route.query[ishikawaModalQueryParam]);
    },
    /** Causes not (yet) linked to a branch */
    branchlessCauses() {
      return this.allCauses.filter(c => c.ishikawaBranch === null);
    },
    /** Problem as it was last set in the state */
    stateProblem() {
      return this.$store.state.problem.problems[`${this.problemId}`];
    },
    /** All the causes of the problem */
    allCauses() {
      if (this.demoMode) {
        return IAHelp.demoCauses(this);
      }
      if (this.stateProblem && this.stateProblem.wCauses) {
        return cloneDeep(this.stateProblem.wCauses);
      }
      return [];
    },
    /** Sorted array of the diagram branches */
    branches() {
      // TODO make branches customizable
      const branches = [
        { id: 1, name: this.$t('message.ISHI_BRANCH_ENVIRONMENT') },
        { id: 2, name: this.$t('message.ISHI_BRANCH_MATERIALS') },
        { id: 3, name: this.$t('message.ISHI_BRANCH_MACHINES') },
        { id: 4, name: this.$t('message.ISHI_BRANCH_METHODS') },
        { id: 5, name: this.$t('message.ISHI_BRANCH_MEN') },
        // For testing purposes
        // { id: 6, name: this.$t('message.ISHI_BRANCH_MACHINES') },
        // { id: 7, name: this.$t('message.ISHI_BRANCH_METHODS') },
        // { id: 8, name: this.$t('message.ISHI_BRANCH_MEN') },
        // { id: 9, name: this.$t('message.ISHI_BRANCH_MEN') },
      ];
      branches.forEach((b) => {
        b.wCauses = this.allCauses.filter(c => c.ishikawaBranch === b.id);
      });
      return branches;
    },
    /** Total number of branches */
    nBranches() {
      return this.branches.length;
    },
    /** Number of branches in the top section of the diagram */
    nTopBranches() {
      return Math.ceil(this.nBranches / 2);
    },
    /** Number of branches in the bottom section of the diagram */
    nBottomBranches() {
      return Math.floor(this.nBranches / 2);
    },
    topBranches() {
      return this.branches.slice(0, this.nTopBranches);
    },
    bottomBranches() {
      return this.branches.slice(this.nTopBranches, this.nBranches);
    },
    /** Style of the right part of the diagram (the 'effect') */
    fishHeadStyle() {
      return {
        left: `${this.coreWidth}px`,
        top: `${this.yCoreLine}px`,
      };
    },
    // Properties used by other components. See src/views/readme.md
    pageTitle() {
      return this.stateProblem ? this.stateProblem.lWhat : this.$t('message.PROBLEM');
    },
  },

  watch: {
    stateProblem(newVal, oldVal) {
      if (this.problemId !== null && !isEqual(newVal, oldVal)) {
        this.$nextTick(this.drawCanvas);
      }
    },
    problemId() {
      if (this.problemId !== null) {
        this.updateWidth();
        setTimeout(() => {
          this.loadingDelayed = true;
          this.$nextTick(this.drawCanvas);
          this.$nextTick(this.setupDroppables);
        }, 100);
      } else {
        Object.assign(this.$data, ishikawaInitialData());
        $(this.$refs.deleteZoneWrapper).droppable('destroy');
        $(this.$refs.deleteZone).droppable('destroy');
      }
    },
  },

  mounted() {
    $(window).on(`resizeEnd.${this.uuid}`, this.updateWidth);
    if (this.problemId !== null) {
      this.loadingDelayed = true;
      this.updateWidth();
      this.$nextTick(this.drawCanvas);
      this.setupDroppables();
    }
  },

  methods: {
    decodeHTML,
    /** A cause item's size was updated */
    causeSizeUpdate() {
      this.$nextTick(this.drawCanvas);
    },
    /** Set the width according to the minimum cause width */
    updateWidth() {
      this.causeKeyModifier++;
      this.$nextTick(this.drawCanvas);
    },
    /** Draw the fishbone */
    drawCanvas() {
      if (!this.$refs.canvas) {
        return;
      }
      const maxTopHeight = this.topBranches.reduce((max, branch) => {
        const height = this.$refs[`branch${branch.id}`][0].$el.offsetHeight;
        return Math.max(max, height);
      }, 0);
      const maxBottomHeight = this.bottomBranches.reduce((max, branch) => {
        const height = this.$refs[`branch${branch.id}`][0].$el.offsetHeight;
        return Math.max(max, height);
      }, 0);
      const { canvas, fishBody } = this.$refs;
      const ctx = canvas.getContext('2d');
      const totalHeight = maxTopHeight + maxBottomHeight + this.sectionMargin;
      this.yCoreLine = maxTopHeight + this.sectionMargin / 2;
      this.coreWidth = fishBody.offsetWidth + fishHeadMargin;
      canvas.width = this.coreWidth;
      canvas.height = totalHeight;
      canvas.style.width = `${this.coreWidth}px`;
      canvas.style.height = `${totalHeight}px`;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.beginPath();
      // Core
      ctx.moveTo(0, this.yCoreLine);
      ctx.lineTo(this.coreWidth, this.yCoreLine);

      // Branches
      this.branches.forEach((branch, i) => {
        const el = this.$refs[`branch${branch.id}`][0].$el;
        const xStartPosition = el.offsetLeft + el.offsetWidth;
        const bHeight = el.offsetHeight;
        const bPaddedHeight = bHeight - branchHeadHeight;
        ctx.moveTo(xStartPosition, this.yCoreLine);
        ctx.lineTo(
          xStartPosition - branchAngleTan * bPaddedHeight,
          i < this.nTopBranches ? this.yCoreLine - bPaddedHeight : this.yCoreLine + bPaddedHeight,
        );
      });
      ctx.closePath();
      ctx.lineWidth = 2;
      ctx.strokeStyle = SColors.mainTextColor;
      ctx.stroke();
    },
    setupDroppables() {
      // Drop on bin
      $(this.$refs.deleteZoneWrapper).droppable();
      $(this.$refs.deleteZone).droppable({
        accept: '.ishikawa-cause',
        tolerance: 'pointer',
        drop: (e, ui) => {
          const helper = ui.helper.length ? ui.helper[0] : ui.helper;
          const { id } = helper.dataset;
          calert(this.$t('message.CONFIRM_DELETE_FW_CAUSE'), [
            {
              title: this.$t('message.DELETE'),
              action: () => {
                this.deleteCause(id);
              },
              sClass: 'red',
            },
            { title: this.$t('message.CANCEL') },
          ]);
        },
      });
    },
    deleteCause(causeId) {
      this.$store.dispatch(`problem/${DELETE_CAUSE}`, { id: causeId })
        .then(() => {
          toastr.remove();
          toastr.success(this.$t('message.CAUSE_DELETED'));
          this.sendSocketMessage({ socketCompany: 1, id: this.problemId, msg: SocketMessages.CC_MSGS.PROBLEM_SHEET });
        })
        .catch((msg) => {
          if (msg === -2) {
            calert(this.$t('message.PB_NO_ACCESS'), [{ title: this.$t('message.OK') }]);
          } else {
            calert(this.$t('message.PB_EDITION_ERROR'), [{ title: this.$t('message.OK') }]);
          }
        });
    },
    /** Sheet selection: - Make the causes element selectable to be displayed in the problem sheet */
    toggleSheetSelection(undetect) {
      // Show/hide sheet selection
      this.sheetSelection = !this.sheetSelection;
      if (this.sheetSelection) {
        detectEscapeKey(this.toggleSheetSelection);
      } else if (undetect) {
        undetectEscapeKey();
      }
    },
    /** An unassigned cause is being dragged */
    unassignedCauseDragging() {
      if (this.$refs.filtersZone && this.$refs.filtersZone.closeOptions instanceof Function) {
        this.$refs.filtersZone.closeOptions();
      }
    },
    createHelpSteps() {
      return [
        new IAHelpStep(null, this.$t('help.ISHIKAWA_INTRO')),
        new IAHelpStep('.fish-head', this.$t('help.ISHIKAWA_EFFECT')),
        new IAHelpStep('.ishikawa-cause', this.$t('help.ISHIKAWA_CAUSE')),
        new IAHelpStep('.ishikawa-branch .branch-head', this.$t('help.ISHIKAWA_BRANCH_DROP')),
        new IAHelpStep('.ishikawa-cause', this.$t('help.ISHIKAWA_CAUSE_DROP')),
        new IAHelpStep('.ishikawa-branch .add-button', this.$t('help.ISHIKAWA_ADD'), () => {
          $('.ishikawa .left-drawer-panel').addClass('display-no-transition');
        }),
        new IAHelpStep('.ishikawa .left-drawer-panel', this.$t('help.ISHIKAWA_UNSORTED'), () => {
          $('.ishikawa .left-drawer-panel').removeClass('display-no-transition');
        }),
      ];
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.ishikawa {
  display: flex;
  flex-flow: column;

  &__toggle-sheet.generic-border.generic-border--text-btn {
    background: white;
  }

  &__wrapper {
    flex: 1;
    min-height: 0px;
    margin: $page-content-padding;
    height: calc(100% - calc(2 * #{$page-content-padding}));
    width: calc(100% - calc(2 * #{$page-content-padding}));
    overflow: auto;
    background: #fff;
    border-radius: $main-border-radius;
  }

  &__hint {
    background: $color-shizen-light-blue-hover;
    padding: 1rem;
    margin: 1rem;
    border-radius: $main-border-radius;

    .icon {
      margin-right: 1rem;
      flex: 0 0 auto;
      width: 1.2rem;
      height: 1.2rem;
    }

    button {
      margin-left: 1rem;
    }
  }

  &__content {
    position: relative;
    // Above sheet-selection
    z-index: 1;

    &__wrapper {
      margin-top: 30px;
      position: relative;
      margin-bottom: 100px;

      .fish-body, .fish-head {
        display: inline-block;
        vertical-align: middle;
      }
      .fish-head {
        width: 18%;
        max-width: 170px;
        position: absolute;
        transform: translateY(-50%);
        background: white;
        border: 2px solid $color-shizen-blue;
        border-top-left-radius: $main-border-radius;
        border-bottom-left-radius: $main-border-radius;
        border-top-right-radius: 100px;
        border-bottom-right-radius: 100px;
        height: 150px;
        padding: .8rem 3rem .8rem 1rem;
        box-sizing: border-box;

        > div.ellipsis-div {
          height: 100%;
        }
      }
      .top-branches, .bottom-branches {
        display: flex;
        align-items: flex-end;
      }
      .bottom-branches {
        align-items: flex-start;
      }
      > canvas {
        position: absolute;
        top: 0px;
        left: 0px;
        width: 100%;
      }
      .sheet-selection {
        position: absolute;
        top: 0px;
        z-index: 2;
      }
    }
  }


  &__delete-zone {
    flex: 0 0 auto;
    background: #fff;
    height: 0px;
    box-sizing: border-box;
    display: flex;
    justify-content: space-between;
    overflow: hidden;

    &.ui-droppable-active {
      height: $buttons-zone-height;
      padding: $buttons-zone-padding;
    }

    .generic-border--red-dashed-border {
      font-weight: 600;

      &.ui-droppable-hover {
        background: $color-shizen-red;
        border-style: solid;
        color: white;
      }
    }
  }

  .sheet-selection-bg {
    position: absolute;
    top: 0px;
    bottom: 0px;
    left: 0px;
    right: 0px;
    background: rgba(0, 0, 0, 0.2);

    h3 {
      text-align: center;
      color: white;
      font-size: 1.5em;
    }
  }
}
</style>
